/**
 * LevelBadge — the lifetime-progression badge, ported from ringmaster's `LevelDiamond`.
 *
 * One component, every surface: beside a name on a lobby seat card, in the entry header's identity
 * pill, blown up on the profile sheet, and at both ends of the match-end XP bar. It is the ONLY
 * place a level is drawn in the DOM, for the same reason `Avatar` is the only place a player disc
 * is — two of these would drift apart inside a week.
 *
 * It renders NOTHING for a null/zero level. That is the whole "no badge until your first finished
 * match" rule, kept here rather than at six call sites: `SeatMirror.level` is 0 until the server
 * has a profile to report, so no caller has to remember it.
 *
 * SVG rather than a rotated div: the design is a double stroke (thick outer, hairline inner) and a
 * rotated box would need counter-rotated text, two nested elements and a non-uniform border. Here
 * it is four points and a `<text>`.
 */
import { MAX_TIER, tierForLevel } from '@spades/shared';

export interface LevelTier {
  /** Thick outer stroke — the tier's identity colour. */
  border: string;
  /** Hairline inner stroke, one step in from the outer. */
  inner: string;
  /** The numeral. */
  text: string;
  /** Halo colour when `glow` is set. */
  glow: string;
  /** Human name, matching `TIER_NAMES` in shared — used by the profile header and aria labels. */
  label: string;
}

/**
 * One band per 5 levels (`tierForLevel`), metals as the user chose them: bronze → silver → gold →
 * platinum → diamond → black spade.
 *
 * Every colour is a CSS var from `theme.css` with a literal fallback, so the palette is retunable
 * from the stylesheet and a badge rendered before the sheet loads still wears its tier. Shaped
 * like `MOTION`/`PERF` in Settings.tsx — the sanctioned pattern here for a per-variant table.
 *
 * The same six metals are drawn on the felt by `LEVEL_TIER_COLORS` in `scene/objects/SeatView.ts`,
 * which needs 0x integers at texture-bake time and so cannot read a var. The fallbacks below, the
 * `--tier-*` tokens and that table are three copies of one palette and must move together; a
 * mismatch shows up as a player's badge being one colour on their seat and another on their lobby
 * card. The glows are the only values that live solely here — Pixi has no drop shadow on the
 * badge — and each is its own band's rim in rgba.
 */
export const LEVEL_TIERS: Record<number, LevelTier> = {
  0: {
    border: 'var(--tier-bronze, #b87333)',
    inner: 'var(--tier-bronze-inner, #7a4a22)',
    text: 'var(--tier-bronze-text, #f0cfae)',
    glow: 'rgba(184, 115, 51, 0.55)',
    label: 'Bronze',
  },
  1: {
    border: 'var(--tier-silver, #c9d2d6)',
    inner: 'var(--tier-silver-inner, #7e8a90)',
    text: 'var(--tier-silver-text, #f4f8fa)',
    glow: 'rgba(201, 210, 214, 0.5)',
    label: 'Silver',
  },
  2: {
    border: 'var(--tier-gold, #e6b866)',
    inner: 'var(--tier-gold-inner, #a9772c)',
    text: 'var(--tier-gold-text, #f9e7b8)',
    glow: 'rgba(230, 184, 102, 0.58)',
    label: 'Gold',
  },
  3: {
    border: 'var(--tier-platinum, #dfe9f0)',
    inner: 'var(--tier-platinum-inner, #93a7b4)',
    text: 'var(--tier-platinum-text, #ffffff)',
    glow: 'rgba(223, 233, 240, 0.5)',
    label: 'Platinum',
  },
  4: {
    border: 'var(--tier-diamond, #7fe4ff)',
    inner: 'var(--tier-diamond-inner, #2c7f99)',
    text: 'var(--tier-diamond-text, #dff7ff)',
    glow: 'rgba(127, 228, 255, 0.58)',
    label: 'Diamond',
  },
  5: {
    border: 'var(--tier-spade, #f4efe6)',
    inner: 'var(--tier-spade-inner, #4a5254)',
    text: 'var(--tier-spade-text, #f4efe6)',
    glow: 'rgba(244, 239, 230, 0.6)',
    label: 'Black Spade',
  },
};

/**
 * Digit cap height as a fraction of the em, in the display face.
 *
 * The numeral's baseline is placed explicitly rather than left to `dominant-baseline`, which
 * browsers resolve from per-font metrics: ringmaster needed a hand-measured `TEXT_Y = 44.5`
 * because Grenze ships old-style figures that hang below the baseline. Bricolage Grotesque has
 * lining figures, so a digit's ink runs baseline → cap height and its centre sits half a cap above
 * the baseline; anchoring `y = 50 + fontSize * CAP_RATIO / 2` puts that centre on the rhombus's
 * widest line at every size.
 *
 * 0.66 is MEASURED, not assumed — `measureText` on the shipped
 * `public/assets/fonts/BricolageGrotesque[opsz,wdth,wght].ttf` at weight 700 reports an ink
 * ascent of 66–67.4 per 100px across every digit, with an overshoot of ~1.4 below the baseline on
 * the round ones. The spread over one-, two- and three-digit labels is under 0.8 units of the
 * 100-unit viewBox, so a single constant centres the whole set. Re-measure it if the display face
 * changes; the generic-sans guess (0.72) sits a visible pixel and a half low at profile size.
 */
const CAP_RATIO = 0.66;

/** The tier a level wears. Exported so callers can preview the NEXT one. */
export function tierOf(level: number): LevelTier {
  return LEVEL_TIERS[tierForLevel(level)] ?? LEVEL_TIERS[MAX_TIER]!;
}

export interface LevelBadgeProps {
  /** Null, zero or absent renders nothing — a player with no finished match has no badge. */
  level: number | null;
  /** Width AND height in px. Default 22, the size it sits at beside a seat name. */
  size?: number;
  /** Outer halo. For the local player and the current level on the match-end bar. */
  glow?: boolean;
  /**
   * Outline-only, muted — the "next level" cap on the bar and the profile's next-tier preview. It
   * still wears the tier it WILL be, which is what turns one more level into a visible reward
   * rather than a bigger number.
   */
  dim?: boolean;
  className?: string;
  /** Accessible label. Defaults to "Level N". */
  title?: string;
}

export function LevelBadge({ level, size = 22, glow = false, dim = false, className, title }: LevelBadgeProps) {
  if (level == null || !Number.isFinite(level) || level < 1) return null;

  const n = Math.floor(level);
  const tier = tierOf(n);
  const label = String(n);
  // Three digits still has to fit inside a rhombus, which is much narrower than its bounding box
  // away from the midline. Sized generously because the badge ships small — a timid numeral is
  // unreadable at 22px, which is where most of these are drawn.
  const fontSize = label.length >= 3 ? 30 : label.length === 2 ? 40 : 50;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={['level-badge', className].filter(Boolean).join(' ')}
      role={title === undefined ? 'img' : undefined}
      aria-label={title ?? `Level ${n}`}
      style={{
        filter: glow ? `drop-shadow(0 0 ${Math.round(size * 0.3)}px ${tier.glow})` : undefined,
        opacity: dim ? 0.45 : 1,
      }}
    >
      {/* Fill first so both strokes sit on top of it. Felt-dark, not black: these are drawn on
          walnut and on the felt gradient, and a pure black plate reads as a hole in both. */}
      <polygon points="50,3 97,50 50,97 3,50" fill={dim ? 'transparent' : 'rgba(23, 19, 16, 0.96)'} />
      <polygon points="50,3 97,50 50,97 3,50" fill="none" stroke={tier.border} strokeWidth={9} strokeLinejoin="round" />
      <polygon
        points="50,21 79,50 50,79 21,50"
        fill="none"
        stroke={dim ? tier.border : tier.inner}
        strokeWidth={3}
        strokeLinejoin="round"
        opacity={0.9}
      />
      <text
        x="50"
        y={50 + (fontSize * CAP_RATIO) / 2}
        textAnchor="middle"
        fill={tier.text}
        style={{
          // Display face: a rank is ceremony, not an operational readout.
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </text>
    </svg>
  );
}
